import Excel from 'exceljs/dist/exceljs.min'
import { parse } from 'date-fns'
import { saveAs } from 'file-saver'
import { getResponseSummary } from '@/api/survey'
import { getPercentage, leadingZero, thousand, capitalize, dateFormatTimezone, getPercentageInt, fixedInt } from '@/libs/utils'
import store from '@/store'
import { cloneDeep, simpleUrl, onlySubdomain } from './utils'
import { getI18n } from '@/libs/i18n'

// const InvalidValue = {
//   ko: '무효 수치',
//   en: 'Invalid Value',
//   ja: '無効値'
// }

const hexToARGB = (value) => {
  return value.replace('#', 'FF').toUpperCase()
}

const calcTime = (time) => {
  let hour = Math.floor(time / 3600)
  time -= hour * 3600
  let minute = Math.floor(time / 60)
  time -= minute * 60
  let second = Math.round(time)
  if (second > 59) {
    second = 0
    minute++
  }
  if (minute > 59) {
    minute = 0
    hour++
  }
  return `${leadingZero(hour)}:${leadingZero(minute)}:${leadingZero(second)}`
}

async function getMarketingRows (journeyOptions, journeyExcelData) {
  const rows = []
  let rowCount = 0

  if (journeyOptions.utm) {
    let utm = {}
    for (const deviceUtm of Object.values(journeyExcelData.utm)) {
      for (const [utmKey, utmValues] of Object.entries(deviceUtm)) {
        if (!utm[utmKey]) {
          utm[utmKey] = []
        }
        utm[utmKey] = utm[utmKey].concat(utmValues)
      }
    }

    const getUtm = (from, ids, tag) => {
      const fromObj = {}
      for (const f of from) {
        const fs = Object.entries(f)[0]
        fromObj[fs[1]] = fs[0]
      }

      const values = []
      for (const id of ids) {
        if (id === null) {
          values.push('Inflow without utm_' + tag + ' parameter')
        } else if (id) {
          values.push(fromObj[id])
        } else {
          values.push('utm_' + tag + '="" or utm_' + tag)
        }
      }
      return values.sort((a, b) => {
        if (a.includes('Inflow without utm_')) {
          return -1
        } else if (b.includes('Inflow without utm_')) {
          return 1
        } else {
          return a.localeCompare(b)
        }
      }).join(', ')
    }

    rows.push({ title: 'Marketing', subTitle: 'Source', value: getUtm(utm.UTM_SOURCE, journeyOptions.utm.source, 'source') })
    rows.push({ title: '', subTitle: 'Medium', value: getUtm(utm.UTM_MEDIUM, journeyOptions.utm.medium, 'medium') })
    rows.push({ title: '', subTitle: 'Campaign', value: getUtm(utm.UTM_CAMPAIGN, journeyOptions.utm.campaign, 'campaign') })
    rows.push({ title: '', subTitle: 'Term', value: getUtm(utm.UTM_TERM, journeyOptions.utm.term, 'term') })
    rows.push({ title: '', subTitle: 'Content', value: getUtm(utm.UTM_CONTENT, journeyOptions.utm.content, 'content') })
    rowCount += 5
  } else if (journeyOptions.survey) {
    let result = await getResponseSummary({
      sid: journeyOptions.sid,
      surveyId: journeyOptions.survey.surveyId,
      start: store.state.journeyPeriod.start,
      end: store.state.journeyPeriod.end
    }, false)

    const survey = {
      ...store.state.surveyList[journeyOptions.sid].find(el => el.surveyId === journeyOptions.survey.surveyId),
      ...result
    }

    rows.push({ title: 'Marketing', subTitle: 'Survey', value: survey.surveyName })
    rowCount += 1

    if (journeyOptions.survey.notTarget) {
      rows.push({ title: '', subTitle: '', value: 'Survey non-target group' })
      rowCount += 1
    }

    if (journeyOptions.survey.notAnswered) {
      rows.push({ title: '', subTitle: '', value: 'Did not respond' })
      rowCount += 1
    }

    const makeArray = (question, all = false) => {
      let array = [...Array(question.answerOptions.maxValue - question.answerOptions.minValue + 1).keys()].map(x => String(question.answerOptions.minValue + x++))
      if (!all) {
        array = array.filter(answer => ((question.answers[answer] || 0) > 0))
      }
      return array
    }

    const allAnswered = () => {
      if (!journeyOptions.survey.answerOptions) return false
      if (Object.keys(journeyOptions.survey.answerOptions).length < Object.keys(survey.statsPerQuestion).length) return false
      for (const question of Object.values(survey.statsPerQuestion)) {
        if (question.qustionType === 'radio' || question.qustionType === 'checkbox') {
          const answers = Object.keys(question.answerOptions).filter(answer => ((question.answers[answer] || 0) > 0))
          if (journeyOptions.survey.answerOptions[question.questionId].length < answers.length) return false
        } else if (question.qustionType === 'range') {
          if (journeyOptions.survey.answerOptions[question.questionId].length < makeArray(question).length) return false
        } else if (question.qustionType === 'text') {
          if (!journeyOptions.survey.answerOptions[question.questionId]) return false
        }
      }
      return true
    }

    if (journeyOptions.survey.answerOptions) {
      rows.push({ title: '', subTitle: '', value: 'Responded' })
      rowCount += 1

      if (!allAnswered()) {
        for (const [key, value] of Object.entries(journeyOptions.survey.answerOptions)) {
          const question = survey.statsPerQuestion[key]
          rows.push({ title: '', subTitle: '', value: question.question })
          rowCount += 1
          if (question.qustionType === 'radio' || question.qustionType === 'checkbox') {
            rows.push({ title: '', subTitle: '', value: value.map(k => question.answerOptions[k]).join(', ') })
            rowCount += 1
          } else if (question.qustionType === 'range') {
            rows.push({ title: '', subTitle: '', value: makeArray(question).filter(el => value.includes(el)).join(', ') })
            rowCount += 1
          }
        }
      }
    }
  }

  return { rows, rowCount }
}

const getCellStyle = {
  border: (value) => {
    value = hexToARGB(value)
    return {
      top: { style: 'thin', color: { argb: value } },
      left: { style: 'thin', color: { argb: value } },
      bottom: { style: 'thin', color: { argb: value } },
      right: { style: 'thin', color: { argb: value } }
    }
  },
  fill: (value) => {
    value = hexToARGB(value)
    return {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: value },
      bcColor: { argb: 'FFFFFFFF' }
    }
  }
}

const coverStyles = {
  gap: {
    border: {
      left: {
        style: 'thick',
        color: { argb: 'FFEC0047' }
      }
    }
  },
  header: {
    font: {
      size: 16,
      bold: true,
      color: { argb: 'FFEC0047' }
    },
    alignment: {
      vertical: 'middle',
      horizontal: 'left'
    }
  },
  title: {
    font: {
      size: 10,
      color: { argb: 'FFEC0047' }
    },
    alignment: {
      vertical: 'middle',
      horizontal: 'left'
    }
  },
  subTitle: {
    font: {
      size: 10,
      color: { argb: 'FFEC0047' }
    },
    alignment: {
      vertical: 'middle',
      horizontal: 'left'
    }
  },
  item: {
    font: {
      size: 10,
      color: { argb: 'FF9FA1A5' }
    },
    alignment: {
      vertical: 'middle',
      horizontal: 'left'
    }
  }
}

const tableStyles = {
  header: {
    font: {
      size: 10,
      color: { argb: hexToARGB('#e7e6e6') }
    },
    fontBoldWhite: {
      size: 10,
      bold: true,
      color: { argb: hexToARGB('#ffffff') }
    },
    alignment: {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true
    },
    border: getCellStyle.border('#2f384b'),
    border2: getCellStyle.border('#b2b2b2'),
    fill: getCellStyle.fill('#262626'),

    sessCnt: getCellStyle.fill('#0e90a5'),
    cnt: getCellStyle.fill('#ec0048'),
    conversion: getCellStyle.fill('#81a507'),
    dropCnt: getCellStyle.fill('#ff4a00'),
    rollCnt: getCellStyle.fill('#9665e3'),
    nullityCnt: getCellStyle.fill('#888888'),
    reloadCnt: getCellStyle.fill('#0074ff'),
    time: getCellStyle.fill('#4b8f0d'),
    'highlighted-sessCnt': getCellStyle.fill('#0a6c7b'),
    'highlighted-cnt': getCellStyle.fill('#b40637'),
    'highlighted-conversion': getCellStyle.fill('#648009'),
    'highlighted-dropCnt': getCellStyle.fill('#cd3c05'),
    'highlighted-rollCnt': getCellStyle.fill('#7851b6'),
    'highlighted-reloadCnt': getCellStyle.fill('#055ccb'),
    'highlighted-time': getCellStyle.fill('#386a0c')
  },
  footer: {
    border: getCellStyle.border('#2f384b'),
    fill: getCellStyle.fill('#262626')
  },
  item: {
    font: {
      size: 10,
      color: { argb: hexToARGB('#2f384b') }
    },
    alignment: {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true
    },
    alignmentLeft: {
      vertical: 'middle',
      horizontal: 'left'
    },
    alignmentNumber: {
      vertical: 'middle',
      horizontal: 'right'
    },
    border: getCellStyle.border('#e7e6e6'),
    focusedFill: getCellStyle.fill('#f2f2f2'),
    highlightedFill: getCellStyle.fill('#ffff00')
  }
}
tableStyles.header.pv = tableStyles.header.cnt
tableStyles.header.stayInCount = tableStyles.header.conversion
tableStyles.header.dropOff = tableStyles.header.dropCnt
tableStyles.header.rollback = tableStyles.header.rollCnt
tableStyles.header.reloadCount = tableStyles.header.reloadCnt
tableStyles.header.avgStay = tableStyles.header.time

class ExcelExport {
  constructor (vue = null) {
    this.now = new Date()
    this.vue = vue
    this.workbook = new Excel.Workbook()
    this.workbook.creator = 'Beusable Analytics'
    this.workbook.created = this.now
    this.workbook.properties.date1904 = true
  }

  setReportInfo (pageData, isSampling, originJourneyData) {
    return new Promise(async (resolve, reject) => {
      const journeyOptions = store.state.journeyOptions

      let sheet = this.workbook.addWorksheet('info')
      sheet.views = [{ showGridLines: false, showRuler: false }]

      let columns = [
        { header: '', key: 'blank', width: 3, height: 25 },
        { header: '', key: 'gap', width: 1, height: 25 },
        { header: '', key: 'title', width: 22.5, height: 25 },
        { header: '', key: 'subTitle', width: 15, height: 25 },
        { header: '', key: 'value', width: 50, height: 25 }
      ]
      sheet.columns = columns

      const journeyExcelData = store.state.journeyExcelData
      const timezoneName = store.state.domain.timezoneName
      let domainName = '*.' + journeyOptions.domain
      if (journeyOptions.subSid) {
        domainName = journeyOptions.subDomain
      }

      let data = []
      data.push({ title: 'Download Info', value: '' })
      data.push({
        title: 'Export Date and Time',
        value: dateFormatTimezone(this.now, timezoneName, 'YYYY-MM-DD HH:mm:ss')
      })
      data.push({
        title: 'Timezone',
        value: timezoneName
      })
      data.push({ blank: '' })

      data.push({ title: 'URL Info', value: '' })
      data.push({ title: 'Report Link', value: window.location.href })
      data.push({ title: 'Domain', value: domainName })
      data.push({ blank: '' })

      data.push({ title: 'Report Info', value: '' })
      data.push({
        title: 'Period',
        value: store.state.journeyPeriod.start + '~' + store.state.journeyPeriod.end
      })

      const device = journeyOptions.device || 'all'

      const marketing = await getMarketingRows(journeyOptions, journeyExcelData)
      data.push(...marketing.rows)
      let moreRowCount = marketing.rowCount

      data.push({
        title: 'Device',
        value: capitalize(device) +
          (device !== 'all' ? ' (' + getPercentage(journeyExcelData.sessionCount[device], journeyExcelData.totalSession).toFixed(1) + '%)' : '')
      })
      const refer = journeyExcelData.refer[device][journeyOptions.refer]
      const totalSession = device !== 'all'
        ? journeyExcelData.sessionCount[device]
        : journeyExcelData.totalSession
      data.push({
        title: 'Referrer',
        value: refer.text +
          (refer.value ? ' (' + getPercentage(refer.value, totalSession).toFixed(1) + '%)' : ' (100.0%)')
      })

      let userType = journeyOptions.exist === null ? 'All' : (journeyOptions.exist ? 'Returning' : 'New')
      data.push({
        title: 'User Type',
        value: userType
      })

      data.push({ title: 'Start Node Session', value: thousand(pageData.sessCnt) })
      data.push({ title: 'Start Node PV', value: thousand(pageData.cnt) })

      sheet.addRows(data)

      let headerRows = [2, 6, 10]
      let titleRows = [3, 4, 7, 8, 11, 12, 13, 14, 15, 16]
      titleRows = titleRows.concat([...Array(moreRowCount).keys()].map(x => 17 + x++))

      let gapColumn = sheet.getColumn('B')
      gapColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        if (headerRows.concat(titleRows).indexOf(rowNumber) !== -1) {
          cell.border = coverStyles.gap.border
        }
      })

      let titleColumn = sheet.getColumn('C')
      titleColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        if (headerRows.indexOf(rowNumber) !== -1) {
          cell.font = coverStyles.header.font
          cell.alignment = coverStyles.header.alignment
        } else if (titleRows.indexOf(rowNumber) !== -1) {
          cell.font = coverStyles.title.font
          cell.alignment = coverStyles.header.alignment
        }
      })

      let subTitleColumn = sheet.getColumn('D')
      subTitleColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        cell.font = coverStyles.subTitle.font
        cell.alignment = coverStyles.subTitle.alignment
      })

      let itemColumn = sheet.getColumn('E')
      itemColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        cell.font = coverStyles.item.font
        cell.alignment = coverStyles.item.alignment
      })

      resolve()
    })
  }

  setReportData (activeNode, isSampling, originData) {
    const i18n = getI18n()
    return new Promise((resolve, reject) => {
      const journeyOptions = store.state.journeyOptions
      let book = this.workbook
      let sheet = book.addWorksheet('Journey')
      sheet.views = [{ showGridLines: false, showRuler: false }]

      let columns = [
        { header: '', key: 'blank', width: 3 },
        { header: 'Sequence', key: 'seq', width: 11 },
        { header: 'Rank', key: 'rank', width: 11 },
        { header: 'Node Title', key: 'title', width: 50 },
        { header: 'Node URL', key: 'linkUrl', width: 50 },
        { header: 'Session', key: 'sessCnt', width: 21 },
        { header: 'PV', key: 'cnt', width: 21 },
        { header: 'Conversion', key: 'conversion', width: 21 },
        { header: 'Drop off', key: 'dropCnt', width: 21 },
        { header: 'Rollback', key: 'rollCnt', width: 21 },
        { header: 'Refresh', key: 'reloadCnt', width: 21 },
        { header: 'Avg. Duration', key: 'time', width: 21 }
      ]

      if (journeyOptions.isCustomNode) {
        columns.splice(4, 1)

        columns[3].header = 'Tag'
        columns[3].key = 'customValue'
      }

      sheet.columns = columns

      let data = []

      activeNode.forEach((node, idx) => {
        if (idx && node && node.seq === idx) {
          let d = cloneDeep(node)
          const rankArray = d.id.split('-')
          d.rank = rankArray[rankArray.length - 1]
          d.sessCnt = isSampling && d.sessCntIsInaccurate ? i18n.t('common.invalidValue') : d.sessCnt
          d.cnt = isSampling && d.sessCntIsInaccurate ? i18n.t('common.invalidValue') : d.cnt
          d.conversion = fixedInt((100 - d.dropOff.toFixed(1)), 2, 1)
          d.dropCnt = fixedInt(d.dropOff, 2, 1)
          d.rollCnt = d.rollback.toFixed(1)
          d.reloadCnt = d.repeat.toFixed(1)
          d.title = (d.title || '').trim()
          d.time = calcTime(d.sessTime)
          if (d.customId === 0) {
            d.customValue = 'Untagged'
          }
          data.push(d)
        }
      })

      data.push({ blank: '' })
      sheet.addRows(data)

      sheet.views = [
        { state: 'frozen', ySplit: 1 }
      ]
      sheet.eachRow(function (row, rowNumber) {
        if (rowNumber === 1) {
        } else if (rowNumber === sheet.rowCount) {
          row.border = tableStyles.footer.border
          row.fill = tableStyles.footer.fill
        } else {
          row.height = 18
          row.border = tableStyles.item.border
          row.font = tableStyles.item.font
          row.alignment = tableStyles.item.alignment
        }
      })

      if (journeyOptions.isCustomNode) {
        'EFGHIJ'.split('').forEach((letter, index) => {
          sheet.getColumn(letter).eachCell({ includeEmpty: true }, function (cell) {
            cell.numFmt = '#,##0'
            cell.alignment = tableStyles.item.alignmentNumber
          })
        })
      } else {
        'FGHIJK'.split('').forEach((letter, index) => {
          sheet.getColumn(letter).eachCell({ includeEmpty: true }, function (cell) {
            cell.numFmt = '#,##0'
            cell.alignment = tableStyles.item.alignmentNumber
          })
        })
      }

      let fillStyleArray = ['', '', '', '', '', '']
      if (journeyOptions.isCustomNode) {
        fillStyleArray = ['', '', '', '', '']
      }
      'sessCnt, cnt, conversion, dropCnt, rollCnt, reloadCnt, time'.split(', ').forEach((key) => fillStyleArray.push(tableStyles.header[key]))

      sheet.eachRow(function (row, rowNumber) {
        if (rowNumber === 1) {
          row.height = 60
          row.border = tableStyles.header.border
          row.font = tableStyles.header.font
          row.alignment = tableStyles.header.alignment
          row.fill = tableStyles.header.fill

          row.eachCell({ includeEmpty: true }, function (cell, colNumber) {
            if (fillStyleArray[colNumber]) {
              cell.font = tableStyles.header.fontBoldWhite
              cell.fill = fillStyleArray[colNumber]
            }
          })
        }
      })

      sheet.getColumn('A').eachCell({ includeEmpty: true }, function (cell) {
        cell.fill = null
        cell.border = null
      })

      resolve()
    })
  }

  setSurveyAnswerData (questions, result, dateString, timezone, timezoneName) {
    return new Promise((resolve, reject) => {
      let book = this.workbook
      let sheet = book.addWorksheet(this.vue.$t('userResearch.survey.excel.allAnswer'))
      sheet.views = [{ showGridLines: false, showRuler: false }]
      let timezoneString = timezoneName

      let columns = [
        { key: 'blank', width: 3 },
        { key: 'date', width: 21 },
        // { key: 'sessionId', width: 21 }, // temp
        { key: 'title', width: 50 },
        { key: 'url', width: 50 }
      ]

      Object.values(questions).forEach(question => {
        columns.push({ key: question.questionId, width: 50 })
      })

      sheet.columns = columns
      sheet.getRow(1).values = ['',
        this.vue.$t('timezone.dashboard.info'),
        timezoneString]
      sheet.getRow(2).values = ['',
        this.vue.$t('userResearch.survey.excel.period'),
        dateString]
      sheet.mergeCells('C1:E1')
      sheet.mergeCells('C2:E2')

      let header = [
        '',
        this.vue.$t('userResearch.survey.excel.submitTime'),
        // 'sessionId', // temp
        this.vue.$t('userResearch.survey.excel.pageName'),
        this.vue.$t('userResearch.survey.excel.pageUrl')
      ]

      Object.values(questions).forEach(question => {
        header.push(question.question)
      })

      sheet.getRow(3).values = header

      let data = []

      Object.values(result).forEach((node, index) => {
        if (typeof node === 'object') {
          const d = {
            date: dateFormatTimezone(parse(node.responseDate), timezoneName, 'YYYY-MM-DD HH:mm:ss'),
            // sessionId: node.sessionId, // temp
            title: node.title.normalize('NFC'),
            url: node.url
          }
          Object.values(questions).forEach(question => {
            const value = node.data ? node.data[question.questionId] : null
            if (value || value === 0) {
              if (question.questionType === 'radio') {
                d[question.questionId] = question.answerOptions[value] || value
              } else if (question.questionType === 'checkbox') {
                d[question.questionId] = value.map(el => (question.answerOptions[el] || el)).join(', ')
              } else {
                d[question.questionId] = value
              }
            } else {
              d[question.questionId] = ''
            }
          })
          data.push(d)
        }
      })

      sheet.addRows(data)

      sheet.views = [
        { state: 'frozen', ySplit: 2 }
      ]
      sheet.eachRow(function (row, rowNumber) {
        if (rowNumber === 1 || rowNumber === 2) {
          row.height = 60
        } else if (rowNumber === 3) {
          row.height = 60
          row.border = tableStyles.header.border
          row.font = tableStyles.header.font
          row.alignment = tableStyles.header.alignment
          row.fill = tableStyles.header.fill
        } else {
          row.border = tableStyles.item.border
          row.font = tableStyles.item.font
          row.alignment = tableStyles.item.alignment
        }
      })
      'BC'.split('').forEach(letter => {
        sheet.getColumn(letter).eachCell({ includeEmpty: true }, function (cell, colNumber) {
          if (colNumber === 1 || colNumber === 2) {
            if (letter === 'B') {
              cell.font = tableStyles.header.fontBoldWhite
              cell.border = tableStyles.header.border2
              cell.alignment = tableStyles.header.alignment
              cell.fill = tableStyles.header.nullityCnt
            } else {
              cell.alignment = tableStyles.header.alignment
            }
          }
        })
      })

      sheet.getColumn('A').eachCell({ includeEmpty: true }, function (cell) {
        cell.fill = null
        cell.border = null
      })

      resolve()
    })
  }

  setSurveySummaryData (countMap, dateString, timezone, timezoneName) {
    return new Promise((resolve, reject) => {
      let book = this.workbook
      let sheet = book.addWorksheet(this.vue.$t('userResearch.survey.excel.answerRate'))
      sheet.views = [{ showGridLines: false, showRuler: false }]

      let timezoneString = timezoneName

      let columns = [
        { key: 'blank', width: 3 },
        { key: 'date', width: 21 },
        { key: 'exposeCount', width: 21 },
        { key: 'answerCount', width: 21 },
        { key: 'answerRate', width: 21 }
      ]
      sheet.columns = columns
      sheet.getRow(1).values = ['',
        this.vue.$t('timezone.dashboard.info'),
        timezoneString]
      sheet.getRow(2).values = ['',
        this.vue.$t('userResearch.survey.excel.period'),
        dateString]
      sheet.mergeCells('C1:E1')
      sheet.mergeCells('C2:E2')
      sheet.getRow(3).values = ['',
        this.vue.$t('userResearch.survey.excel.date'),
        this.vue.$t('userResearch.survey.excel.exposeCount'),
        this.vue.$t('userResearch.survey.excel.answerCount'),
        this.vue.$t('userResearch.survey.excel.answerRate')]

      let data = []

      Object.entries(countMap).forEach((node, idx) => {
        data.push({
          date: node[0],
          exposeCount: node[1].exposed,
          answerCount: node[1].answered,
          answerRate: node[1].exposed ? (node[1].answered / node[1].exposed) : 0
        })
      })

      data.reverse()
      sheet.addRows(data)

      sheet.views = [
        { state: 'frozen', ySplit: 2 }
      ]
      sheet.eachRow(function (row, rowNumber) {
        if (rowNumber === 1 || rowNumber === 2) {
          row.height = 60
        } else if (rowNumber === 3) {
          row.height = 60
          row.border = tableStyles.header.border
          row.font = tableStyles.header.font
          row.alignment = tableStyles.header.alignment
          row.fill = tableStyles.header.fill
        } else {
          row.height = 18
          row.border = tableStyles.item.border
          row.font = tableStyles.item.font
          row.alignment = tableStyles.item.alignment
        }
      })
      'BC'.split('').forEach(letter => {
        sheet.getColumn(letter).eachCell({ includeEmpty: true }, function (cell, colNumber) {
          if (colNumber === 1 || colNumber === 2) {
            if (letter === 'B') {
              cell.font = tableStyles.header.fontBoldWhite
              cell.border = tableStyles.header.border2
              cell.alignment = tableStyles.header.alignment
              cell.fill = tableStyles.header.nullityCnt
            } else {
              cell.alignment = tableStyles.header.alignment
            }
          }
        })
      })
      'CDE'.split('').forEach(letter => {
        sheet.getColumn(letter).eachCell({ includeEmpty: true }, function (cell, colNumber) {
          if (colNumber > 2) {
            cell.numFmt = letter === 'E' ? '#,##0%' : '#,##0'
            cell.alignment = tableStyles.item.alignmentNumber
          }
        })
      })

      sheet.getColumn('A').eachCell({ includeEmpty: true }, function (cell) {
        cell.fill = null
        cell.border = null
      })

      resolve()
    })
  }

  setRankingInfo (infoData, isSampling, originRankingData) {
    return new Promise(async (resolve, reject) => {
      const journeyOptions = store.state.journeyOptions
      const journeyExcelData = store.state.journeyExcelData || {}

      let sheet = this.workbook.addWorksheet('info')
      sheet.views = [{ showGridLines: false, showRuler: false }]

      let columns = [
        { header: '', key: 'blank', width: 3, height: 25 },
        { header: '', key: 'gap', width: 1, height: 25 },
        { header: '', key: 'title', width: 22.5, height: 25 },
        { header: '', key: 'subTitle', width: 15, height: 25 },
        { header: '', key: 'value', width: 50, height: 25 }
      ]
      sheet.columns = columns

      const timezoneName = store.state.domain.timezoneName
      let domainName = '*.' + journeyOptions.domain
      if (journeyOptions.subSid) {
        domainName = journeyOptions.subDomain
      }

      let data = []
      data.push({ title: 'Download Info', value: '' })
      data.push({
        title: 'Export Date and Time',
        value: dateFormatTimezone(this.now, timezoneName, 'YYYY-MM-DD HH:mm:ss')
      })
      data.push({
        title: 'Timezone',
        value: timezoneName
      })
      data.push({ blank: '' })

      data.push({ title: 'URL Info', value: '' })
      data.push({ title: 'Report Link', value: window.location.href })
      data.push({ title: 'Domain', value: domainName })
      data.push({ blank: '' })

      data.push({ title: 'Report Info', value: '' })
      data.push({
        title: 'Period',
        value: store.state.journeyPeriod.start + '~' + store.state.journeyPeriod.end
      })

      data.push({ title: 'Sub-domain', value: infoData.subDomain || 'All' })
      data.push({ title: 'Bookmark', value: infoData.bookmark ? 'On' : 'Off' })
      if (infoData.keyword) {
        data.push({ title: `Search [${infoData.term}]`, value: infoData.keyword })
      }
      data.push({ title: journeyOptions.isCustomNode ? 'Total Tag' : 'Total URL', value: thousand(infoData.count) })

      data.push({ title: 'Total PV', value: thousand(infoData.pv) })

      const marketing = await getMarketingRows(journeyOptions, journeyExcelData)
      data.push(...marketing.rows)

      const device = journeyOptions.device || 'all'
      data.push({
        title: 'Device',
        value: capitalize(device) +
          (device !== 'all' && journeyExcelData.sessionCount ? ' (' + getPercentage(journeyExcelData.sessionCount[device], journeyExcelData.totalSession).toFixed(1) + '%)' : '')
      })
      if (journeyExcelData.refer && journeyExcelData.refer[device]) {
        const refer = journeyExcelData.refer[device][journeyOptions.refer]
        const totalSession = device !== 'all'
          ? journeyExcelData.sessionCount[device]
          : journeyExcelData.totalSession
        data.push({
          title: 'Referrer',
          value: refer.text +
            (refer.value ? ' (' + getPercentage(refer.value, totalSession).toFixed(1) + '%)' : ' (100.0%)')
        })
      }
      let userType = journeyOptions.exist === null ? 'All' : (journeyOptions.exist ? 'Returning' : 'New')
      data.push({ title: 'User Type', value: userType })

      data.push({ title: 'Sorting', value: infoData.sort })

      sheet.addRows(data)

      let headerRows = [2, 6, 10]
      let blankRows = [5, 9]
      let titleRows = []
      for (let i = 2; i <= data.length + 1; i++) {
        if (!headerRows.includes(i) && !blankRows.includes(i)) {
          titleRows.push(i)
        }
      }
      let gapColumn = sheet.getColumn('B')
      gapColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        if (headerRows.concat(titleRows).indexOf(rowNumber) !== -1) {
          cell.border = coverStyles.gap.border
        }
      })

      let titleColumn = sheet.getColumn('C')
      titleColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        if (headerRows.indexOf(rowNumber) !== -1) {
          cell.font = coverStyles.header.font
          cell.alignment = coverStyles.header.alignment
        } else if (titleRows.indexOf(rowNumber) !== -1) {
          cell.font = coverStyles.title.font
          cell.alignment = coverStyles.header.alignment
        }
      })

      let subTitleColumn = sheet.getColumn('D')
      subTitleColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        cell.font = coverStyles.subTitle.font
        cell.alignment = coverStyles.subTitle.alignment
      })

      let itemColumn = sheet.getColumn('E')
      itemColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        cell.font = coverStyles.item.font
        cell.alignment = coverStyles.item.alignment
      })

      resolve()
    })
  }

  setRankingData (listData, sort, firstRank, group, isSampling, originRankingData) {
    const i18n = getI18n()
    const journeyOptions = store.state.journeyOptions
    return new Promise((resolve, reject) => {
      let book = this.workbook
      let sheet = book.addWorksheet('Ranking')
      sheet.views = [{ showGridLines: false, showRuler: false }]

      let columns = [
        { header: '', key: 'blank', width: 3 },
        { header: 'Rank', key: 'rank', width: 11 },
        { header: 'Domain', key: 'domain', width: 30 },
        { header: 'Page info [title]', key: 'title', width: 50 },
        { header: 'Page info [URL]', key: 'url', width: 50 },
        { header: (sort === 'pv' ? '★ ' : '') + 'PV', key: 'count', width: 21 },
        { header: (sort === 'stayInCount' ? '★ ' : '') + 'Conversion', key: 'stayInCount', width: 21 },
        { header: (sort === 'dropOff' ? '★ ' : '') + 'Drop off', key: 'dropOff', width: 21 },
        { header: (sort === 'rollback' ? '★ ' : '') + 'Rollback', key: 'rollback', width: 21 },
        { header: (sort === 'reloadCount' ? '★ ' : '') + 'Refresh', key: 'reloadCount', width: 21 },
        { header: (sort === 'avgStay' ? '★ ' : '') + 'Avg. Duration', key: 'time', width: 21 }
      ]

      if (journeyOptions.isCustomNode) {
        columns.splice(4, 1)
        columns.splice(3, 1)

        columns[2].header = 'Tag'
        columns[2].key = 'customValue'
      }

      if (group) {
        columns.splice(2, 0, {
          header: 'Tag',
          key: 'tag',
          width: 20
        })
      }

      sheet.columns = columns

      let data = []
      const types = {
        pv: 'count',
        stayInCount: 'stayInCount',
        dropOff: 'dropOff',
        rollback: 'rollback',
        reloadCount: 'reloadCount',
        avgStay: 'avgStay'
      }

      let formerValue = null
      let lastRank = 0

      listData.forEach((node, idx) => {
        if (node) {
          let d = cloneDeep(node)
          let currentValue = d[types[sort]]
          if (['stayInCount', 'dropOff', 'rollback', 'reloadCount'].includes(sort)) {
            currentValue = getPercentage(currentValue, d.count)
          } else if (sort === 'avgStay') {
            currentValue = Math.round(currentValue / 1000)
          }
          if (currentValue !== formerValue) {
            d.rank = firstRank < 2 ? idx + 1 : firstRank - idx
          } else {
            d.rank = lastRank
          }
          formerValue = currentValue
          lastRank = d.rank
          d.url = journeyOptions.matchType === 'exact' ? d.url : simpleUrl(d.url)
          d.domain = d.url.split('/')[0]
          d.time = calcTime(d.avgStay / 1000)
          for (const key in types) {
            if (key !== 'pv' && key !== 'avgStay') {
              d[key] = key === 'dropOff' || key === 'stayInCount'
                ? getPercentageInt(d[key], d.count, 2, 1) + '%'
                : getPercentage(d[key], d.count).toFixed(1) + '%'
            }
          }
          d.count = d.countIsInaccurate ? i18n.t('common.invalidValue') : d.count

          if (group) {
            d.tag =
              d.tagInfo && d.tagInfo[group].tagId > -1
                ? onlySubdomain(d.tagInfo[group].tagName, journeyOptions.domain)
                : 'Untagged'
          }

          if (d.customId === 0) {
            d.customValue = 'Untagged'
          }

          data.push(d)
        }
      })

      data.push({ blank: '' })
      sheet.addRows(data)

      sheet.views = [
        { state: 'frozen', ySplit: 1 }
      ]
      sheet.eachRow(function (row, rowNumber) {
        if (rowNumber === 1) {
        } else if (rowNumber === sheet.rowCount) {
          row.border = tableStyles.footer.border
          row.fill = tableStyles.footer.fill
        } else {
          row.height = 18
          row.border = tableStyles.item.border
          row.font = tableStyles.item.font
          row.alignment = tableStyles.item.alignment
        }
      })

      if (journeyOptions.isCustomNode) {
        'DEFGHI'.split('').forEach((letter, index) => {
          sheet.getColumn(letter).eachCell({ includeEmpty: true }, function (cell) {
            cell.numFmt = '#,##0'
            cell.alignment = tableStyles.item.alignmentNumber
          })
        })
      } else if (group) {
        'GHIJK'.split('').forEach((letter, index) => {
          sheet.getColumn(letter).eachCell({ includeEmpty: true }, function (cell) {
            cell.numFmt = '#,##0'
            cell.alignment = tableStyles.item.alignmentNumber
          })
        })
      } else {
        'FGHIJ'.split('').forEach((letter, index) => {
          sheet.getColumn(letter).eachCell({ includeEmpty: true }, function (cell) {
            cell.numFmt = '#,##0'
            cell.alignment = tableStyles.item.alignmentNumber
          })
        })
      }

      let fillStyleArray = ['', '', '', '', '', '']
      if (journeyOptions.isCustomNode) {
        fillStyleArray = ['', '', '', '']
      } else if (group) {
        fillStyleArray = ['', '', '', '', '', '', '']
      }
      const columnKeys = 'pv, stayInCount, dropOff, rollback, reloadCount, avgStay'.split(', ')
      columnKeys.forEach((key) => fillStyleArray.push(tableStyles.header[key]))

      let focusedCellIndex = -1
      for (let i = 0; i < columnKeys.length; i++) {
        if (sort === columnKeys[i]) {
          focusedCellIndex = i + 6
          break
        }
      }

      sheet.eachRow(function (row, rowNumber) {
        if (rowNumber === 1) {
          row.height = 60
          row.border = tableStyles.header.border
          row.font = tableStyles.header.font
          row.alignment = tableStyles.header.alignment
          row.fill = tableStyles.header.fill

          row.eachCell({ includeEmpty: true }, function (cell, colNumber) {
            if (fillStyleArray[colNumber]) {
              cell.font = tableStyles.header.fontBoldWhite
              cell.fill = fillStyleArray[colNumber]
            }
          })
        } else if (rowNumber !== sheet.rowCount) {
          row.eachCell({ includeEmpty: true }, function (cell, colNumber) {
            if (focusedCellIndex === colNumber) {
              cell.fill = tableStyles.item.focusedFill
            }
          })
        }
      })

      sheet.getColumn('A').eachCell({ includeEmpty: true }, function (cell) {
        cell.fill = null
        cell.border = null
      })

      resolve()
    })
  }

  setTrendInfo (highlightInfo, isSampling, originTrendData) {
    return new Promise(async (resolve, reject) => {
      const journeyOptions = store.state.journeyOptions

      let sheet = this.workbook.addWorksheet('info')
      sheet.views = [{ showGridLines: false, showRuler: false }]

      let columns = [
        { header: '', key: 'blank', width: 3, height: 25 },
        { header: '', key: 'gap', width: 1, height: 25 },
        { header: '', key: 'title', width: 22.5, height: 25 },
        { header: '', key: 'subTitle', width: 15, height: 25 },
        { header: '', key: 'value', width: 50, height: 25 }
      ]
      sheet.columns = columns

      const journeyExcelData = store.state.journeyExcelData
      const timezoneName = store.state.domain.timezoneName

      let domainName = '*.' + journeyOptions.domain
      if (journeyOptions.subSid) {
        domainName = journeyOptions.subDomain
      }

      let data = []
      data.push({ title: 'Download Info', value: '' })
      data.push({
        title: 'Export Date and Time',
        value: dateFormatTimezone(this.now, timezoneName, 'YYYY-MM-DD HH:mm:ss')
      })
      data.push({
        title: 'Timezone',
        value: timezoneName
      })
      data.push({ blank: '' })

      data.push({ title: 'URL Info', value: '' })
      data.push({ title: 'Report Link', value: window.location.href })
      data.push({ title: 'Domain', value: domainName })
      data.push({ blank: '' })

      data.push({ title: 'Report Info', value: '' })
      data.push({
        title: 'Period',
        value: store.state.journeyPeriod.start + '~' + store.state.journeyPeriod.end
      })

      const device = journeyOptions.device || 'all'

      const marketing = await getMarketingRows(journeyOptions, journeyExcelData)
      data.push(...marketing.rows)

      data.push({
        title: 'Device',
        value: capitalize(device) +
          (device !== 'all' ? ' (' + getPercentage(journeyExcelData.sessionCount[device], journeyExcelData.totalSession).toFixed(1) + '%)' : '')
      })
      const refer = journeyExcelData.refer[device][journeyOptions.refer]
      const totalSession = device !== 'all'
        ? journeyExcelData.sessionCount[device]
        : journeyExcelData.totalSession
      data.push({
        title: 'Referrer',
        value: refer.text +
          (refer.value ? ' (' + getPercentage(refer.value, totalSession).toFixed(1) + '%)' : ' (100.0%)')
      })

      let userType = journeyOptions.exist === null ? 'All' : (journeyOptions.exist ? 'Returning' : 'New')
      data.push({
        title: 'User Type',
        value: userType
      })

      if (highlightInfo) {
        data.push({
          title: 'Highlight Node',
          subTitle: 'Sequence',
          value: highlightInfo.sequence
        })
        data.push({
          title: '',
          subTitle: 'Rank',
          value: highlightInfo.rank
        })
        data.push({
          title: '',
          subTitle: journeyOptions.isCustomNode ? 'Tag' : 'Node URL',
          value: journeyOptions.isCustomNode ? (highlightInfo.customId === 0 ? 'Untagged' : highlightInfo.customValue) : highlightInfo.url
        })
      }

      sheet.addRows(data)

      let headerRows = [2, 6, 10]
      let blankRows = [5, 9]
      let titleRows = []
      for (let i = 2; i <= data.length + 1; i++) {
        if (!headerRows.includes(i) && !blankRows.includes(i)) {
          titleRows.push(i)
        }
      }
      let gapColumn = sheet.getColumn('B')
      gapColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        if (headerRows.concat(titleRows).indexOf(rowNumber) !== -1) {
          cell.border = coverStyles.gap.border
        }
      })

      let titleColumn = sheet.getColumn('C')
      titleColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        if (headerRows.indexOf(rowNumber) !== -1) {
          cell.font = coverStyles.header.font
          cell.alignment = coverStyles.header.alignment
        } else if (titleRows.indexOf(rowNumber) !== -1) {
          cell.font = coverStyles.title.font
          cell.alignment = coverStyles.header.alignment
        }
      })

      let subTitleColumn = sheet.getColumn('D')
      subTitleColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        cell.font = coverStyles.subTitle.font
        cell.alignment = coverStyles.subTitle.alignment
      })

      let itemColumn = sheet.getColumn('E')
      itemColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        cell.font = coverStyles.item.font
        cell.alignment = coverStyles.item.alignment
      })

      resolve()
    })
  }

  setTrendData (nodesDataArray, nodeMap, thruData, trendMap, isSampling, originTrendData) {
    const i18n = getI18n()
    return new Promise((resolve, reject) => {
      const journeyOptions = store.state.journeyOptions
      let book = this.workbook
      let sheet = book.addWorksheet('Trend')
      sheet.views = [{ showGridLines: false, showRuler: false }]

      let numMaxSequences = 20
      for (let i = 0; i < 20; i++) {
        if (!trendMap[i] || !trendMap[i].length) {
          numMaxSequences = i
          break
        }
      }

      let columns = [
        { header: '', key: 'blank', width: 3 },
        { header: 'Sequence', key: 'seq', width: 11 },
        { header: 'Rank', key: 'rank', width: 11 },
        { header: 'Node Title', key: 'title', width: 50 },
        { header: 'Node URL', key: 'linkUrl', width: 50 }
      ]

      if (journeyOptions.isCustomNode) {
        columns.splice(4, 1)
        columns[3].header = 'Tag'
        columns[3].key = 'customValue'
      }

      const headerNames = 'Session, PV, Conversion, Drop off, Rollback, Refresh, Avg. Duration'.split(', ')
      const displayableThruDataKeys = ['sessCnt', 'conversion', 'dropCnt']

      'sessCnt, cnt, conversion, dropCnt, rollCnt, reloadCnt, time'.split(', ').forEach((key, index) => {
        columns.push({ header: headerNames[index], key: key, width: 21 })
        if (thruData && displayableThruDataKeys.indexOf(key) > -1) {
          columns.push({ header: 'Highlight ' + headerNames[index], key: 'highlighted-' + key, width: 21 })
        }
      })

      sheet.columns = columns

      let data = []
      let highlitedRowIndex = -1

      const sumNextSessCnt = (nodeData) => {
        let sum = 0
        nodeData.nextArray && nodeData.nextArray.forEach((nextData) => {
          sum += nextData.sessCnt
        })
        return sum
      }

      nodesDataArray.forEach((sequence, sequenceIndex) => {
        sequence.nodesArray.forEach((nodeData, nodeIndex) => {
          let d = cloneDeep(nodeData)
          const nodeInfo = nodeMap[nodeData._id] || {}

          if (nodeInfo.customId === 0) {
            nodeInfo.customValue = 'Untagged'
          }

          d.seq = sequenceIndex + 1
          if (nodeData._id === '-1') {
            d.rank = 'Other'
            d.title = ''
            d.linkUrl = thousand(nodeData.urlCnt) + ' URLs'
          } else {
            d.rank = nodeIndex + 1
            d.title = (nodeInfo.title || '').trim()
            d.linkUrl = nodeInfo.linkUrl || ''
          }

          if (journeyOptions.isCustomNode) {
            d.customValue = nodeInfo.customValue
          }

          d.dropCnt = getPercentageInt(d.dropCnt, d.sessCnt, 2, 1) + '%'
          d.rollCnt = d.rollback.toFixed(1) + '%'
          d.reloadCnt = d.repeat.toFixed(1) + '%'

          d.conversion = sequenceIndex === numMaxSequences - 1 ? 'SEQ limit' : sumNextSessCnt(d) || (sequenceIndex === nodesDataArray.length - 1 ? 'add next SEQ' : 0)
          if (d.conversion !== 'SEQ limit' && d.conversion !== 'add next SEQ') {
            d.conversion = d.conversion ? getPercentageInt(d.conversion, d.sessCnt, 2, 1) + '%' : '0.0%'
          }
          d.time = calcTime(d.sessTime)

          d.tempSessCnt = d.sessCnt
          if (isSampling) {
            d.cnt = d.sessCntIsInaccurate ? i18n.t('common.invalidValue') : d.cnt
            d.sessCnt = d.sessCntIsInaccurate ? i18n.t('common.invalidValue') : d.sessCnt
          }

          if (thruData) {
            const thruNodeData = thruData.seqMap[sequenceIndex] ? thruData.seqMap[sequenceIndex].nodes[nodeData._id] : null
            displayableThruDataKeys.forEach((key) => {
              if (thruNodeData) {
                if (key === 'conversion') {
                  d['highlighted-' + key] = sequenceIndex === numMaxSequences - 1 ? 'SEQ limit' : sumNextSessCnt(thruNodeData)
                } else {
                  d['highlighted-' + key] = thruNodeData[key] || 0
                }
              } else {
                d['highlighted-' + key] = key === 'conversion' && sequenceIndex === numMaxSequences - 1 ? 'SEQ limit' : 0
              }
            })
            if (sequenceIndex === thruData._sequenceIndex && nodeIndex === thruData._nodeIndex) {
              highlitedRowIndex = data.length + 2
            }

            if (isSampling) {
              d['highlighted-conversion'] = d['highlighted-conversion'] ? getPercentageInt(d['highlighted-conversion'], d['tempSessCnt'], 2, 1) + '%' : 0.0 + '%'
              d['highlighted-dropCnt'] = d['highlighted-dropCnt'] ? getPercentageInt(d['highlighted-dropCnt'], d['tempSessCnt'], 2, 1) + '%' : 0.0 + '%'
              d['highlighted-sessCnt'] = thruNodeData && thruNodeData.sessCntIsInaccurate ? i18n.t('common.invalidValue') : d['highlighted-sessCnt']
            }
          }
          data.push(d)
        })
      })

      data.push({ blank: '' })
      sheet.addRows(data)

      sheet.views = [
        { state: 'frozen', ySplit: 1 }
      ]
      sheet.eachRow(function (row, rowNumber) {
        if (rowNumber === 1) {
        } else if (rowNumber === sheet.rowCount) {
          row.border = tableStyles.footer.border
          row.fill = tableStyles.footer.fill
        } else {
          row.height = 18
          row.border = tableStyles.item.border
          row.font = tableStyles.item.font
          row.alignment = tableStyles.item.alignment
        }
      })

      let fillStyleArray = ['', '', '', '', '', '']
      if (journeyOptions.isCustomNode) {
        fillStyleArray = ['', '', '', '', '']
      }
      let columnKeys = 'sessCnt, cnt, conversion, dropCnt, rollCnt, reloadCnt, time'.split(', ')
      if (thruData) {
        displayableThruDataKeys.forEach((key) => {
          columnKeys.splice(columnKeys.indexOf(key) + 1, 0, 'highlighted-' + key)
        })
      }
      columnKeys.forEach((key) => fillStyleArray.push(tableStyles.header[key]))

      if (journeyOptions.isCustomNode) {
        'EFGHIJKLMNOP'.substr(0, columnKeys.length).split('').forEach((letter, index) => {
          if (columnKeys[index].indexOf('time') < 0) {
            sheet.getColumn(letter).eachCell({ includeEmpty: true }, function (cell) {
              cell.numFmt = '#,##0'
              cell.alignment = tableStyles.item.alignmentNumber
            })
          }
        })
      } else {
        'FGHIJKLMNOPQ'.substr(0, columnKeys.length).split('').forEach((letter, index) => {
          if (columnKeys[index].indexOf('time') < 0) {
            sheet.getColumn(letter).eachCell({ includeEmpty: true }, function (cell) {
              cell.numFmt = '#,##0'
              cell.alignment = tableStyles.item.alignmentNumber
            })
          }
        })
      }

      const focusedCellIndexs = columnKeys.map((key) => key.indexOf('highlighted-') > -1)
      focusedCellIndexs.unshift(false, false, false, false, false, false)

      sheet.eachRow(function (row, rowNumber) {
        if (rowNumber === 1) {
          row.height = 60
          row.border = tableStyles.header.border
          row.font = tableStyles.header.font
          row.alignment = tableStyles.header.alignment
          row.fill = tableStyles.header.fill

          row.eachCell({ includeEmpty: true }, function (cell, colNumber) {
            if (fillStyleArray[colNumber]) {
              cell.font = tableStyles.header.fontBoldWhite
              cell.fill = fillStyleArray[colNumber]
            }
          })
        } else if (rowNumber !== sheet.rowCount) {
          row.eachCell({ includeEmpty: true }, function (cell, colNumber) {
            if (rowNumber === highlitedRowIndex && colNumber > 1 && colNumber < (journeyOptions.isCustomNode ? 5 : 6)) {
              cell.fill = tableStyles.item.highlightedFill
            } else if (focusedCellIndexs[colNumber]) {
              cell.fill = tableStyles.item.focusedFill
            }
          })
        }
      })

      sheet.getColumn('A').eachCell({ includeEmpty: true }, function (cell) {
        cell.fill = null
        cell.border = null
      })

      resolve()
    })
  }

  setTargetingInfo (targetInfo, isForwardDirection, isLandingSession, connectionStrength, isSampling, originData) {
    return new Promise(async (resolve, reject) => {
      const journeyOptions = store.state.journeyOptions

      let sheet = this.workbook.addWorksheet('info')
      sheet.views = [{ showGridLines: false, showRuler: false }]

      let columns = [
        { header: '', key: 'blank', width: 3, height: 25 },
        { header: '', key: 'gap', width: 1, height: 25 },
        { header: '', key: 'title', width: 22.5, height: 25 },
        { header: '', key: 'subTitle', width: 15, height: 25 },
        { header: '', key: 'value', width: 50, height: 25 }
      ]
      sheet.columns = columns

      const journeyExcelData = store.state.journeyExcelData
      const timezoneName = store.state.domain.timezoneName
      let domainName = '*.' + journeyOptions.domain
      if (journeyOptions.subSid) {
        domainName = journeyOptions.subDomain
      }

      let data = []
      data.push({ title: 'Download Info', value: '' })
      data.push({
        title: 'Export Date and Time',
        value: dateFormatTimezone(this.now, timezoneName, 'YYYY-MM-DD HH:mm:ss')
      })
      data.push({
        title: 'Timezone',
        value: timezoneName
      })
      data.push({ blank: '' })

      data.push({ title: 'URL Info', value: '' })
      data.push({ title: 'Report Link', value: window.location.href })
      data.push({ title: 'Domain', value: domainName })
      data.push({ blank: '' })

      data.push({ title: 'Report Info', value: '' })
      data.push({
        title: 'Period',
        value: store.state.journeyPeriod.start + '~' + store.state.journeyPeriod.end
      })

      const device = journeyOptions.device || 'all'

      const marketing = await getMarketingRows(journeyOptions, journeyExcelData)
      data.push(...marketing.rows)

      data.push({
        title: 'Device',
        value: capitalize(device) +
          (device !== 'all' ? ' (' + getPercentage(journeyExcelData.sessionCount[device], journeyExcelData.totalSession).toFixed(1) + '%)' : '')
      })
      const refer = journeyExcelData.refer[device][journeyOptions.refer]
      const totalSession = device !== 'all'
        ? journeyExcelData.sessionCount[device]
        : journeyExcelData.totalSession
      data.push({
        title: 'Referrer',
        value: refer.text +
          (refer.value ? ' (' + getPercentage(refer.value, totalSession).toFixed(1) + '%)' : ' (100.0%)')
      })

      let userType = journeyOptions.exist === null ? 'All' : (journeyOptions.exist ? 'Returning' : 'New')
      data.push({
        title: 'User Type',
        value: userType
      })

      if (journeyOptions.isCustomNode) {
        data.push({
          title: 'Target',
          subTitle: 'Tag',
          value: targetInfo.customId === 0 ? 'Untagged' : targetInfo.customValue
        })
      } else {
        data.push({
          title: 'Target',
          subTitle: 'Page URL',
          value: targetInfo.linkUrl
        })

        data.push({
          title: '',
          subTitle: 'Page Title',
          value: targetInfo.title
        })
        data.push({
          title: '',
          subTitle: 'Journey',
          value: isForwardDirection ? 'Subsequent' : 'Preceding'
        })
      }

      data.push({
        title: '',
        subTitle: 'Connection',
        value: connectionStrength === 100 ? 'All (100%)' : 'Top ' + Math.max(10, connectionStrength) + '%'
      })

      isForwardDirection && data.push({
        title: '',
        subTitle: 'Session',
        value: isLandingSession ? 'Landing' : 'Passage'
      })

      sheet.addRows(data)

      let headerRows = [2, 6, 10]
      let blankRows = [5, 9]
      let titleRows = []
      for (let i = 2; i <= data.length + 1; i++) {
        if (!headerRows.includes(i) && !blankRows.includes(i)) {
          titleRows.push(i)
        }
      }

      let gapColumn = sheet.getColumn('B')
      gapColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        if (headerRows.concat(titleRows).indexOf(rowNumber) !== -1) {
          cell.border = coverStyles.gap.border
        }
      })

      let titleColumn = sheet.getColumn('C')
      titleColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        if (headerRows.indexOf(rowNumber) !== -1) {
          cell.font = coverStyles.header.font
          cell.alignment = coverStyles.header.alignment
        } else if (titleRows.indexOf(rowNumber) !== -1) {
          cell.font = coverStyles.title.font
          cell.alignment = coverStyles.header.alignment
        }
      })

      let subTitleColumn = sheet.getColumn('D')
      subTitleColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        cell.font = coverStyles.subTitle.font
        cell.alignment = coverStyles.subTitle.alignment
      })

      let itemColumn = sheet.getColumn('E')
      itemColumn.eachCell({ includeEmpty: true }, function (cell, rowNumber) {
        cell.font = coverStyles.item.font
        cell.alignment = coverStyles.item.alignment
      })

      resolve()
    })
  }

  setTargetingData (nodesDataArray, nodeMap, isForwardDirection, isSampling, originData, moreSeq = false, moreRank = false) {
    const i18n = getI18n()
    if (moreSeq) nodesDataArray = nodesDataArray.slice(0, -1)
    if (moreRank) {
      nodesDataArray[0][0]._children = nodesDataArray[0][0]._children.slice(0, -1)
      nodesDataArray[1] = nodesDataArray[1].slice(0, -1)
    }
    return new Promise((resolve, reject) => {
      const journeyOptions = store.state.journeyOptions

      let book = this.workbook
      let sheet = book.addWorksheet('Targeting Journey')
      sheet.views = [{ showGridLines: false, showRuler: false }]

      let columns = [
        { header: '', key: 'blank', width: 3 }
      ]

      const headerNames = 'Session, PV, Refresh, Avg. Duration'.split(', ')
      const columnKeys = 'sessCnt, cnt, reloadCnt, time'.split(', ')
      if (isForwardDirection) {
        headerNames.splice(2, 0, 'Drop off')
        columnKeys.splice(2, 0, 'dropCnt')
      }

      let data = []

      const traverse = (nodeData, callback) => {
        if (nodeData._seq >= nodesDataArray.length) return
        callback(nodeData)
        nodeData._children && nodeData._children.forEach((child) => {
          child._display && traverse(child, callback)
        })
      }

      let numMaxSequences = -1
      traverse(nodesDataArray[0][0], (nodeData) => {
        nodeData._rowspan = 1
        numMaxSequences = Math.max(numMaxSequences, nodeData._seq)
      })
      for (let sequenceIndex = numMaxSequences; sequenceIndex > -1; sequenceIndex--) {
        nodesDataArray[sequenceIndex].forEach((nodeData) => {
          if (nodeData._display && nodeData._parent) {
            nodeData._parent._rowspan += nodeData._rowspan
          }
        })
      }

      for (let sequenceName, columnKey, sequenceIndex = 0; sequenceIndex <= numMaxSequences; sequenceIndex++) {
        if (isForwardDirection) {
          columnKey = sequenceIndex
          sequenceName = (columnKey ? '+' : '') + columnKey
        } else {
          columnKey = numMaxSequences - sequenceIndex
          sequenceName = (columnKey ? '-' : '') + columnKey
        }
        headerNames.push(sequenceName)
        columnKeys.push('seq' + columnKey)
      }

      const cellLetters = 'GHIJKLMNOPQRSTUVWXYZ'.split('')
      if (isForwardDirection) {
        cellLetters.push('AA')
      } else {
        cellLetters.unshift('F')
        cellLetters.length = numMaxSequences + 1
        cellLetters.reverse()
      }

      traverse(nodesDataArray[0][0], (nodeData) => {
        const d = { ...nodeData }
        const nodeInfo = nodeMap[nodeData._id] || {}
        d.time = calcTime(d.sessTime / 1000)
        d['seq' + nodeData._seq] = nodeInfo.linkUrl || ''
        if (journeyOptions.isCustomNode) {
          if (nodeInfo.customId === 0) {
            d['seq' + nodeData._seq] = 'Untagged'
          } else {
            d['seq' + nodeData._seq] = nodeInfo.customValue
          }
        }
        d.cellLetter = [cellLetters[nodeData._seq]]

        if (isSampling) {
          if (isForwardDirection) {
            d['dropCnt'] = getPercentage(d['dropCnt'], d.sessCnt).toFixed(1) + '%'
          }
          d['reloadCnt'] = getPercentage(d['reloadCnt'], d.cnt).toFixed(1) + '%'
          d['sessCnt'] = d.sessCntIsInaccurate ? i18n.t('common.invalidValue') : d['sessCnt']
          d['cnt'] = d.sessCntIsInaccurate ? i18n.t('common.invalidValue') : d['cnt']
        }

        data.push(d)
      })

      data.push({ blank: '' })

      const xSplitIndex = isForwardDirection ? 6 : 5

      const sequenceColorPresets = isForwardDirection ? ['#1f2718', '#2a3816', '#3d5315', '#537112', '#627f10', '#81990e'] : ['#082444', '#10376a', '#236aac', '#2c7cb8', '#3a9ac8', '#42a9d0']

      const cellStyleArray = ['', '']
      columnKeys.forEach((key, index) => {
        const sequenceIndex = index - xSplitIndex + 1
        if (sequenceIndex < 0) {
          cellStyleArray.push({
            font: tableStyles.header.fontBoldWhite,
            fill: tableStyles.header[key]
          })
        } else {
          cellStyleArray.push({
            width: 30,
            font: { ...tableStyles.header.fontBoldWhite, size: 14 },
            fill: getCellStyle.fill(sequenceColorPresets[isForwardDirection ? Math.min(5, sequenceIndex) : 5 - Math.max(0, 5 - numMaxSequences + sequenceIndex)])
          })
        }
      })

      columnKeys.forEach((key, index) => {
        columns.push({ header: headerNames[index], key: key, width: (/^seq[0-9]/).test(key) ? 31 : 21 })
      })
      sheet.columns = columns
      sheet.addRows(data)

      sheet.views = [
        { state: 'frozen', xSplit: xSplitIndex, ySplit: 1 }
      ]

      const contentCellAlignment = {
        vertical: 'top',
        horizontal: 'left'
      }

      sheet.eachRow(function (row, rowNumber) {
        if (rowNumber === 1) {
          row.height = 60
          row.border = tableStyles.header.border
          row.alignment = tableStyles.header.alignment
          row.fill = tableStyles.header.fill

          row.eachCell({ includeEmpty: true }, function (cell, colNumber) {
            const style = cellStyleArray[colNumber]
            if (style) {
              cell.font = style.font
              cell.fill = style.fill
            }
          })
        } else if (rowNumber === sheet.rowCount) {
          row.border = tableStyles.footer.border
          row.fill = tableStyles.footer.fill
        } else {
          row.height = 18
          row.border = tableStyles.item.border
          row.font = tableStyles.item.font
          row.alignment = tableStyles.item.alignment

          row.eachCell({ includeEmpty: true }, function (cell, colNumber) {
            if (colNumber < xSplitIndex) {
              cell.numFmt = '#,##0'
              cell.alignment = tableStyles.item.alignmentNumber
            } else if (colNumber > xSplitIndex) {
              cell.alignment = contentCellAlignment
            }
          })
        }
      })

      const getMergeCellName = (letter, index, rows) => {
        return letter + (index + 2) + ':' + letter + (index + 2 + rows - 1)
      }

      data.forEach((item, index) => {
        if (item._rowspan && item._rowspan > 1) {
          sheet.mergeCells(getMergeCellName(item.cellLetter, index, item._rowspan))
        }
      })

      sheet.getColumn('A').eachCell({ includeEmpty: true }, function (cell) {
        cell.fill = null
        cell.border = null
      })

      resolve()
    })
  }

  setTargetingPageInfo (nodesDataArray, nodeMap, targetUrlId, isSampling, originData) {
    return new Promise((resolve, reject) => {
      const journeyOptions = store.state.journeyOptions
      let book = this.workbook
      let sheet = book.addWorksheet('Page Info')
      sheet.views = [{ showGridLines: false, showRuler: false }]

      let columns = [
        { header: '', key: 'blank', width: 3 },
        { header: 'No.', key: 'no', width: 11 },
        { header: 'Page info [Domain]', key: 'domain', width: 34 },
        { header: 'Page info [Path]', key: 'path', width: 50 },
        { header: 'Page info [Title]', key: 'title', width: 50 }
      ]

      if (journeyOptions.isCustomNode) {
        columns.splice(4, 1)
        columns.splice(3, 1)

        columns[2].header = 'Tag'
        columns[2].key = 'customValue'
      }

      sheet.columns = columns

      const data = []
      const ids = []

      const numNodesData = nodesDataArray.length
      for (let sequenceIndex = 0; sequenceIndex < numNodesData; sequenceIndex++) {
        nodesDataArray[sequenceIndex].forEach((nodeData) => {
          const id = nodeData._id
          if (nodeData._display && ids.indexOf(id) < 0) {
            const nodeMapData = nodeMap[id]
            data.push({
              _id: parseInt(id),
              domain: nodeMapData.linkHost,
              path: nodeMapData.linkPath,
              title: nodeMapData.title,
              customValue: nodeMapData.customId === 0 ? 'Untagged' : (nodeMapData.customId === null ? '' : nodeMapData.customValue)
            })
            ids.push(id)
          }
        })
      }

      data.sort((a, b) => {
        if (a.domain > b.domain) {
          return 1
        }
        if (a.domain < b.domain) {
          return -1
        }
        if (a.path > b.path) {
          return 1
        }
        if (a.path < b.path) {
          return -1
        }
        if (a.title > b.title) {
          return 1
        }
        if (a.title < b.title) {
          return -1
        }
        if (a.customValue < b.customValue) {
          return -1
        }
        return 0
      })

      let highlitedRowIndex = -1
      data.forEach((item, index) => {
        item.no = index + 1
        if (item._id === targetUrlId) {
          highlitedRowIndex = index + 2
        }
      })

      data.push({ blank: '' })
      sheet.addRows(data)

      sheet.views = [
        { state: 'frozen', ySplit: 1 }
      ]
      sheet.eachRow(function (row, rowNumber) {
        if (rowNumber === 1) {
          row.height = 60
          row.border = tableStyles.header.border
          row.font = tableStyles.header.font
          row.alignment = tableStyles.header.alignment
          row.fill = tableStyles.header.fill
        } else if (rowNumber === sheet.rowCount) {
          row.border = tableStyles.footer.border
          row.fill = tableStyles.footer.fill
        } else {
          row.height = 18
          row.border = tableStyles.item.border
          row.font = tableStyles.item.font
          row.alignment = tableStyles.item.alignmentLeft
          row.eachCell({ includeEmpty: true }, function (cell, colNumber) {
            if (rowNumber === highlitedRowIndex && colNumber > 2 && colNumber < 6) {
              cell.fill = tableStyles.item.highlightedFill
            }
          })
        }
      })

      sheet.getColumn('B').eachCell({ includeEmpty: true }, function (cell) {
        cell.alignment = tableStyles.item.alignment
      })

      sheet.getColumn('A').eachCell({ includeEmpty: true }, function (cell) {
        cell.fill = null
        cell.border = null
      })

      resolve()
    })
  }

  download (fileName) {
    return new Promise((resolve, reject) => {
      fileName +=
        '_' + this.now.getFullYear() + '-' + leadingZero(this.now.getMonth() + 1) + '-' + leadingZero(this.now.getDate()) +
        'T' + leadingZero(this.now.getHours()) + '-' + leadingZero(this.now.getMinutes()) + '-' + leadingZero(this.now.getSeconds()) +
        '.xlsx'
      this.workbook.xlsx.writeBuffer()
        .then(function (data) {
          let blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
          saveAs(blob, fileName)
          resolve()
        })
        .catch((error) => {
          reject(error)
        })
    })
  }
}

export default ExcelExport
